<template>
  <div id="app">
    <ImpersonationBanner />
    <router-view />
    <LoadingGuard />
    <MeetingWindow />
    <Toaster richColors position="top-center" />
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import io from 'socket.io-client'
import { computed, onBeforeMount, ref, watch, watchEffect } from 'vue'
import { z } from 'zod'

import 'vue-sonner/style.css'
import { Toaster } from '@/components/ui/sonner'
import { addLoginHistoryMutation } from '@/features/auth/api'
import { getUserData } from '@/features/auth/api'
import LoadingGuard from '@/features/auth/components/loading-guard/loading-guard.vue'
import useAccount from '@/features/auth/hooks/use-account.hook'
import useIsAuth from '@/features/auth/hooks/use-is-auth.hook'
import useLoginSession from '@/features/auth/hooks/use-login-session.hook'
import usePhone from '@/features/auth/hooks/use-phone.hook'
import authStore from '@/features/auth/stores/auth/auth.store'
import { PermissionsGroupsEnumType } from '@/features/auth/types'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import MeetingWindow from '@/features/meetings/components/meeting-window/meeting-window.vue'
import ImpersonationBanner from '@/features/ui/components/impersonation-banner/impersonation-banner.vue'
import useProgram from '@/features/programs/hooks/use-program.hook'
import usePrograms from '@/features/programs/hooks/use-programs.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import { useImpersonation } from '@/features/admin/users/hooks/use-impersonation.hook'
import useRouter from '@/hooks/use-router.hook'
import useWhiteLabel from '@/hooks/use-white-label.hook'
import { ANALYTICS_EVENTS, identifyUser, trackEvent } from '@/lib/posthog'
import { ROUTES } from '@/utils/config/constants'
import { invalidateConversations } from '@/utils/lib/vue-query'
import { setAppTitle } from '@/utils/misc/document-title'
import convertHexToRGB from '@/utils/misc/hex-to-rgb'
import { registerE2eModalHook } from '@/utils/e2e/modal-hook'

registerE2eModalHook()

const router = useRouter()
const loginSession = useLoginSession()

const urlParams = new URLSearchParams(window.location.search)
const programId = z.string().uuid().safeParse(urlParams.get('programId')).data
if (programId) {
  programsStore.setSelectedProgramId(programId)
}

const { isAuthRef } = useIsAuth()
const { accountRef } = useAccount()
const { phoneRef } = usePhone()
const { whiteLabelRef } = useWhiteLabel()
const { programsRef } = usePrograms()
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const { programRef } = useProgram(computed(() => selectedProgramIdRef.value))
const { programRolesRef } = useProgramRoles()
const { isImpersonating } = useImpersonation()

const accountIdRef = computed(() => accountRef.value?.id)
const accountSupportIdRef = computed(() => accountRef.value?.supportId)
const accountFirstNameRef = computed(() => accountRef.value?.firstName)
const accountLastNameRef = computed(() => accountRef.value?.lastName)
const accountEmailRef = computed(() => accountRef.value?.email)
const accountPhoneRef = computed(() => phoneRef.value)
const accountRoleRef = computed(() => programRolesRef.value)
const accountProgramRef = computed(() => programRef.value?.name)
const accountUserIdRef = computed(() => accountRef.value?.id)

const socketRef = ref<any>(null)

// Initialize Socket.IO connection with authentication when user is authenticated
watch(
  [isAuthRef, accountIdRef],
  ([isAuth, accountId]) => {
    if (isAuth && accountId && authStore.authToken) {
      // Close existing connection if any
      if (socketRef.value) {
        socketRef.value.disconnect()
      }

      // Create new authenticated connection
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      socketRef.value = io(import.meta.env.VITE_API_URL, {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        reconnectionAttempts: import.meta.env.VITE_ENV === 'development' ? 1 : 100,
        auth: {
          token: authStore.authToken,
          'x-csrf-token': 'socketio-request'
        }
      })

      // Listen for connection errors
      socketRef.value.on('connect_error', (error: any) => {
        console.error('Socket.IO connection failed:', error.message)
      })

      // Re-establish socket listeners
      listenSocket(accountId)
    } else if (socketRef.value) {
      // Disconnect if user is not authenticated
      socketRef.value.disconnect()
      socketRef.value = null
    }
  },
  { immediate: true, flush: 'post' }
)

// set the user active permission group based on the program roles (if not already set)
// the admin and manager have priority over the user for the permission group and displayed interface when logging in
watch(programRolesRef, (programRoles) => {
  if (programRoles?.length === 0) return
  if (authStore.userActivePermissionGroup !== null) return

  if (programRoles?.includes(RoleEnumTypeV2.ADMIN) || programRoles?.includes(RoleEnumTypeV2.MANAGER)) {
    authStore.setUserActivePermissionGroup(PermissionsGroupsEnumType.ADMIN)
  } else {
    authStore.setUserActivePermissionGroup(PermissionsGroupsEnumType.USER)
  }
})

// when the user is auth for the first time, get its user data
watch(isAuthRef, async (isAuth) => {
  if (isAuth) {
    const userData = await getUserData()
    if (userData) {
      // await authStore.setUserRole(userData.role)
      authStore.setUserLanguage(userData.language)
      // On ne lance la redirection post-login que si on est encore sur une page
      // publique (login, callback SSO). Sinon, un flux dédié a déjà navigué vers
      // /redirect (qui restitue le deep-link) et il ne faut pas l'écraser.
      if (router?.currentRoute.matched.some((record) => record.meta.public)) {
        router?.push(ROUTES.REDIRECT).catch(() => {})
      }
    }
  }
})

// Socket listeners are now handled in the authentication watcher above

// identify user to posthog
watch(
  [accountRef, programsRef],
  ([account, programs]) => {
    if (account) {
      identifyUser(account, programs)
    }
  },
  { immediate: true, flush: 'post' },
)

// call activity mutation to update last user activity on mounted
const { mutate: addLoginHistory } = useMutation({
  mutationFn: addLoginHistoryMutation,
  onError: undefined,
})

watch(accountIdRef, (accountId) => {
  if (accountId) {
    addLoginHistory()
  }
})

// add visitor id to brevo
watch(accountSupportIdRef, (supportId) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  if (supportId && window.BrevoConversationsSetup) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    window.BrevoConversationsSetup.visitorId = supportId
  }
})

const brevoIsReadyRef = ref(false)
const whiteLabelStyleId = 'white-label-styles'
const whiteLabelFaviconId = 'white-label-favicon'

watchEffect(() => {
  if (!whiteLabelRef.value) return

  // setFavicon
  if (whiteLabelRef.value.favIcon) {
    let linkFavicon = document.getElementById(whiteLabelFaviconId) as HTMLLinkElement
    if (!linkFavicon) {
      linkFavicon = document.createElement('link')
      linkFavicon.id = whiteLabelFaviconId
      linkFavicon.rel = 'icon'
      document.querySelector('head')!.appendChild(linkFavicon)
    }
    linkFavicon.href = whiteLabelRef.value.favIcon
  } else {
    const existingFavicon = document.getElementById(whiteLabelFaviconId)
    if (existingFavicon) {
      existingFavicon.remove()
    }
  }

  // setPWA manifest (dynamic from API)
  const existingManifest = document.querySelector('link[rel="manifest"]')
  if (!existingManifest) {
    const linkManifest = document.createElement('link')
    linkManifest.rel = 'manifest'
    linkManifest.href = `${import.meta.env.VITE_API_URL}/v1/data/manifest.json`
    document.querySelector('head')!.appendChild(linkManifest)
  }

  // setAppleTouchIcon for iOS PWA
  const existingAppleIcon = document.querySelector('link[rel="apple-touch-icon"]')
  if (!existingAppleIcon && whiteLabelRef.value.favIcon) {
    const linkAppleIcon = document.createElement('link')
    linkAppleIcon.rel = 'apple-touch-icon'
    linkAppleIcon.href = whiteLabelRef.value.favIcon
    document.querySelector('head')!.appendChild(linkAppleIcon)
  }

  // setThemeColor for Android PWA
  const existingThemeColor = document.querySelector('meta[name="theme-color"]')
  if (existingThemeColor) {
    existingThemeColor.setAttribute('content', whiteLabelRef.value.colorPrimary ?? '#3936BD')
  } else {
    const metaThemeColor = document.createElement('meta')
    metaThemeColor.name = 'theme-color'
    metaThemeColor.content = whiteLabelRef.value.colorPrimary ?? '#3936BD'
    document.querySelector('head')!.appendChild(metaThemeColor)
  }

  // setAppleMobileWebAppTitle for iOS
  const existingAppleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]')
  if (existingAppleTitle) {
    existingAppleTitle.setAttribute('content', whiteLabelRef.value.companyTitle ?? '')
  } else {
    const metaAppleTitle = document.createElement('meta')
    metaAppleTitle.name = 'apple-mobile-web-app-title'
    metaAppleTitle.content = whiteLabelRef.value.companyTitle ?? ''
    document.querySelector('head')!.appendChild(metaAppleTitle)
  }

  // set custom style + set colors
  const colorPrimary = whiteLabelRef.value.colorPrimary
  const colorSecondary = whiteLabelRef.value.colorSecondary
  if (colorPrimary && colorSecondary && colorPrimary.trim() !== '' && colorSecondary.trim() !== '') {
    let style = document.getElementById(whiteLabelStyleId) as HTMLStyleElement
    if (!style) {
      style = document.createElement('style')
      style.id = whiteLabelStyleId
      document.querySelector('head')!.appendChild(style)
    }
    const rgbPrimary = convertHexToRGB(colorPrimary)
    const rgbSecondary = convertHexToRGB(colorSecondary)
    style.innerHTML = `:root {--color-white-label-primary: ${rgbPrimary}; --color-white-label-secondary: ${rgbSecondary}; --bs-primary-focus: rgba(${rgbPrimary}, 0.5); --bs-primary-light: rgba(${rgbPrimary}, 0.7); --color-primary: rgb(${rgbPrimary});--color-secondary: rgb(${rgbSecondary});--bs-primary: rgb(${rgbPrimary});--bs-secondary: rgb(${rgbSecondary}); --primary: rgb(${rgbPrimary}); --secondary: rgb(${rgbSecondary}); --ring: rgb(${rgbPrimary});}`
    if (whiteLabelRef.value.customStyle) {
      style.innerHTML += whiteLabelRef.value.customStyle
    }
  } else {
    const existingStyle = document.getElementById(whiteLabelStyleId)
    if (existingStyle) {
      existingStyle.remove()
    }
  }

  // setAppTitle — le titre complet est recomposé avec le sujet de la page
  // courante (WCAG 2.4.2), cf. utils/misc/document-title.
  setAppTitle(whiteLabelRef.value.companyTitle)

  // inject brevo script (disabled during impersonation)
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  if (window.brevoScript && !isImpersonating.value) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    document.querySelector('head')!.appendChild(window.brevoScript)
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  if (window.BrevoConversations && !isImpersonating.value) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    window.BrevoConversationsSetup = {
      colors: {
        buttonText: '#f0f0f0',
        buttonBg: `${whiteLabelRef.value.colorPrimary}`,
      },
      customWidgetButton: '.custom-chat-button',
      locale: {
        customStrings: {
          welcome: `Une question ? Nous sommes là pour vous aider ! \n\n Envoyez-nous un message ici ou contactez-nous par téléphone au ${whiteLabelRef.value.supportPhone}.`,
          offlineNote: `Nous sommes hors ligne. Laissez-nous un message ici ou par e-mail sur ${whiteLabelRef.value.supportEmail}.`,
        },
      },
    }

    brevoIsReadyRef.value = true
  }
})

// add user data to brevo (disabled during impersonation)
watch(
  [
    accountEmailRef,
    accountFirstNameRef,
    accountLastNameRef,
    accountPhoneRef,
    accountRoleRef,
    accountProgramRef,
    accountUserIdRef,
    brevoIsReadyRef,
    isImpersonating,
  ],
  ([email, firstName, lastName, phone, roles, programName, userId, _, isImpersonatingValue]) => {
    // Skip if impersonating
    if (isImpersonatingValue) return

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    if (brevoIsReadyRef.value) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      window.BrevoConversations('updateIntegrationData', {
        email: email ?? '',
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        phone: phone ?? '',
        role: roles?.join(',') ?? '',
        programName: programName ?? '',
        userId: userId ?? '',
      })
    }
  },
  { flush: 'post' },
)

// watch for SAML token and error
onBeforeMount(async () => {
  var url_string = window.location.href
  var url = new URL(url_string)

  // Handle error code parameter from SAML authentication
  if (url.searchParams.get('errorCode')) {
    const { useToast } = await import('vue-toastification')
    const { getGlobalI18nInstance } = await import('@/lib/i18n')
    const toast = useToast()
    const i18n = getGlobalI18nInstance()
    const errorCode = url.searchParams.get('errorCode')
    const errorId = url.searchParams.get('errorId') || ''

    // Map error codes to i18n keys
    const errorI18nKeys: Record<string, string> = {
      SAML_INTERNAL_ERROR: 'auth.saml.error.internalError',
      SAML_AUTH_FAILED: 'auth.saml.error.authFailed',
      SAML_SIGNATURE_ERROR: 'auth.saml.error.signatureError',
      SAML_USER_NOT_REGISTERED: 'auth.saml.error.userNotRegistered',
    }

    const i18nKey = errorI18nKeys[errorCode!] || 'auth.saml.error.internalError'

    // Attendre que i18n soit prêt et utiliser l'instance globale
    if (i18n) {
      // Pour l'erreur USER_NOT_REGISTERED, on n'affiche pas l'errorId (c'est un message user-friendly)
      const errorMessage = errorCode === 'SAML_USER_NOT_REGISTERED'
        ? i18n.global.t(i18nKey)
        : i18n.global.t(i18nKey, { errorId })
      toast.error(String(errorMessage))
    } else {
      // Fallback si i18n n'est pas encore initialisé
      toast.error(`Une erreur s'est produite lors de l'authentification. Code d'erreur : ${errorId}`)
    }

    // Remove error parameters from URL
    url.searchParams.delete('errorCode')
    url.searchParams.delete('errorId')
    window.history.replaceState({}, '', url.toString())
  }

  if (url.searchParams.get('token')) {
    try {
      await authStore.setAuthToken(url.searchParams.get('token')!)
      // KC-439 : login_complete du flux SSO. L'app reboote sur /?token=… ;
      // les timings et le deep-link sont restaurés depuis le sessionStorage.
      loginSession.restore()
      trackEvent(ANALYTICS_EVENTS.AUTH_LOGIN_COMPLETE, {
        auth_method: 'sso',
        time_to_login_ms: loginSession.timeSinceStart(),
        failed_attempts_in_session: 0,
        used_password_reset: false,
        is_first_login: false,
        program_id: programsStore.selectedProgramId,
        role: null,
        had_program_deep_link: loginSession.hadProgramDeepLink(),
        program_context_preserved: loginSession.programContextPreserved(),
      })
      loginSession.reset()
      // KC-439 : retirer le token de l'URL pour ne pas re-déclencher
      // login_complete à chaque rechargement de page (l'app reste sur /?token=…).
      url.searchParams.delete('token')
      window.history.replaceState({}, '', url.toString())
    } catch (error) {
      console.error(error)
    }
  }
})

// listen to socket
function listenSocket(accountId: string) {
  if (!socketRef.value) return

  socketRef.value.off(`USER_${accountId}`)
  socketRef.value.on(`USER_${accountId}`, (payload: any) => {
    switch (payload.action) {
      case 'SEND_MESSAGE':
        // invalidate the conversations query when the socket receives a new message
        invalidateConversations()
        break
      default:
        break
    }
  })
}
</script>
